import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, ChevronLeft, ChevronRight, Heart, ShieldCheck, Star, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { ImageWithFallback } from "./ImageWithFallback";
import { useDesktopPointer } from "./HomeLiveElements";
import { getRooms, getReviews, fmtAmount, locationText } from "@/lib/stayflowApi";
import { isWishlisted, toggleWishlist } from "@/lib/wishlist";

const HeartParticles = () => (
  <span className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
    {[...Array(6)].map((_, i) => {
      const a = (i / 6) * Math.PI * 2;
      return (
        <motion.span key={i}
          initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
          animate={{ opacity: 0, x: Math.cos(a) * 18, y: Math.sin(a) * 18, scale: 0.3 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="absolute w-1.5 h-1.5 rounded-full bg-brand"
        />
      );
    })}
  </span>
);

export const PropertyCard = ({ property, index = 0, query = "", locationOverride = null, nights = 0, onDelete = null, distanceLabel = null }) => {
  const { id, name, cover_image, min_price_per_night, min_price_incl_gst } = property;
  const isDemo = !!property.__demo;
  const geoLoc = locationOverride ? [locationOverride.city, locationOverride.state].filter(Boolean).join(", ") : "";
  const loc = geoLoc || locationText(property);
  const inclPrice = min_price_incl_gst ?? min_price_per_night;
  const price = fmtAmount(inclPrice);
  const total = nights > 0 && typeof inclPrice === "number" ? fmtAmount(inclPrice * nights) : null;

  const ownRating = Number(property.rating ?? property.avg_rating ?? property.average_rating ?? 0);
  const ownReviews = Number(property.reviews_count ?? property.review_count ?? property.total_reviews ?? 0);

  // Fall back to live Google rating (backend caches 1h) when the API has none.
  const reviewsQ = useQuery({
    queryKey: ["reviews", id],
    queryFn: () => getReviews(id),
    staleTime: 60 * 60 * 1000,
    retry: 1,
    enabled: ownRating <= 0 && !isDemo,
  });
  const rating = ownRating > 0 ? ownRating : Number(reviewsQ.data?.rating || 0);
  const reviews = ownRating > 0 ? ownReviews : Number(reviewsQ.data?.total || 0);

  // Gather all property images: cover + own photos + every room photo (rooms query cached)
  const roomsQ = useQuery({ queryKey: ["rooms", id], queryFn: () => getRooms(id), staleTime: 5 * 60 * 1000, retry: 1, enabled: !isDemo });
  const images = React.useMemo(() => {
    const ownPhotos = (property.photos || []).map((p) => p.url);
    const roomPics = (roomsQ.data || []).flatMap((r) => [r.cover_image, ...((r.photos || []).map((p) => p.url))]);
    return [...new Set([cover_image, ...ownPhotos, ...roomPics].filter(Boolean))];
  }, [roomsQ.data, cover_image, property.photos]);

  const [idx, setIdx] = useState(0);
  const desktop = useDesktopPointer();
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, lift: 0 });
  const [burst, setBurst] = useState(0);
  const hoverTO = React.useRef(null);
  const cycleIV = React.useRef(null);

  const onCardMove = (e) => {
    if (!desktop) return;
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: +(-py * 6).toFixed(2), ry: +(px * 6).toFixed(2), lift: -6 });
  };
  const onCardEnter = () => {
    if (!desktop || images.length < 2) return;
    hoverTO.current = setTimeout(() => {
      const n = Math.min(3, images.length);
      cycleIV.current = setInterval(() => setIdx((i) => (i + 1) % n), 1800);
    }, 300);
  };
  const onCardLeave = () => {
    setTilt({ rx: 0, ry: 0, lift: 0 });
    if (hoverTO.current) clearTimeout(hoverTO.current);
    if (cycleIV.current) clearInterval(cycleIV.current);
    hoverTO.current = null; cycleIV.current = null;
    if (desktop) setIdx(0);
  };
  useEffect(() => () => { if (hoverTO.current) clearTimeout(hoverTO.current); if (cycleIV.current) clearInterval(cycleIV.current); }, []);

  const [saved, setSaved] = useState(() => isWishlisted(id));
  useEffect(() => {
    const on = () => setSaved(isWishlisted(id));
    window.addEventListener("sf-wishlist", on);
    return () => window.removeEventListener("sf-wishlist", on);
  }, [id]);
  const toggleSave = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setBurst((b) => b + 1);
    setSaved(toggleWishlist(id));
  }, [id]);

  useEffect(() => { if (idx >= images.length) setIdx(0); }, [images.length, idx]);

  const go = (e, dir) => {
    e.preventDefault(); e.stopPropagation();
    setIdx((i) => (i + dir + images.length) % images.length);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.3) }}
      style={{ perspective: 1000 }}
    >
      <div
        onMouseMove={onCardMove}
        onMouseEnter={onCardEnter}
        onMouseLeave={onCardLeave}
        className="transition-transform duration-200 ease-out will-change-transform"
        style={{ transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) translateY(${tilt.lift}px)` }}
      >
      <Link to={isDemo ? "#" : `/stays/${id}${query}`} onClick={isDemo ? (e) => e.preventDefault() : undefined} data-testid={`property-card-${id}`} className={`group block ${isDemo ? "cursor-default" : ""}`}>
        <div
          className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-paper-muted shadow-sm group-hover:shadow-md lg:group-hover:shadow-xl transition-shadow"
          data-testid={`property-slideshow-${id}`}
        >
          {images.length === 0 && <ImageWithFallback src={cover_image} alt={name} className="w-full h-full transition-transform duration-500 group-hover:scale-105" />}
          {images.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={`${name} ${i + 1}`}
              loading="lazy"
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${i === idx ? "opacity-100" : "opacity-0"}`}
            />
          ))}

          {/* Verified / Demo badge (frosted) */}
          {isDemo ? (
            <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 rounded-full bg-amber-400/95 text-amber-950 text-[0.7rem] font-bold px-2.5 py-1 shadow-sm">
              DEMO
            </span>
          ) : (
            <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 rounded-full bg-white/85 backdrop-blur-md text-ink text-[0.7rem] font-semibold px-2.5 py-1 shadow-sm">
              <ShieldCheck className="w-3.5 h-3.5 text-brand" aria-hidden="true" /> Verified Stay
            </span>
          )}

          {onDelete && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(id); }}
              aria-label="Delete demo stay"
              data-testid={`demo-delete-${id}`}
              className="absolute bottom-2.5 right-2.5 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-brand flex items-center justify-center shadow active:scale-95 transition"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}

          {/* Wishlist heart */}
          <button
            onClick={toggleSave}
            aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
            data-testid={`wishlist-${id}`}
            className="absolute top-2.5 right-2.5 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
          >
            <motion.span
              key={burst}
              animate={burst ? { scale: [1, 1.35, 0.9, 1.1, 1] } : {}}
              transition={{ duration: 0.4 }}
              className="relative flex items-center justify-center"
            >
              <Heart className={`w-6 h-6 drop-shadow ${saved ? "fill-brand text-brand" : "fill-black/25 text-white"}`} />
              {burst > 0 && saved && <HeartParticles key={`p${burst}`} />}
            </motion.span>
          </button>

          {images.length > 1 && (
            <>
              <button aria-label="Previous photo" onClick={(e) => go(e, -1)} data-testid={`slide-prev-${id}`}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronLeft className="w-4 h-4 text-ink" />
              </button>
              <button aria-label="Next photo" onClick={(e) => go(e, 1)} data-testid={`slide-next-${id}`}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronRight className="w-4 h-4 text-ink" />
              </button>
              <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
                {images.map((src, i) => (
                  <span key={src} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-white" : "w-1.5 bg-white/60"}`} />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="pt-3">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-heading font-bold text-[1.05rem] text-ink tracking-tight line-clamp-1">{name}</h3>
            {rating > 0 ? (
              <span className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-ink" data-testid={`rating-${id}`}>
                <Star className="w-3.5 h-3.5 fill-ink text-ink" /> {rating.toFixed(2)}
                {reviews > 0 && <span className="text-ink-faint font-normal">({reviews})</span>}
              </span>
            ) : (
              <span className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-ink" data-testid={`rating-${id}`}><Star className="w-3.5 h-3.5 fill-ink text-ink" /> New</span>
            )}
          </div>

          {loc && (
            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-ink-soft line-clamp-1">
              <MapPin className="w-3.5 h-3.5 text-brand shrink-0" aria-hidden="true" /> {loc}
            </p>
          )}

          {distanceLabel && (
            <p className="mt-0.5 text-sm font-semibold text-ink" data-testid={`card-distance-${id}`}>{distanceLabel}</p>
          )}

          <p className="mt-2 text-ink">
            <span className="font-heading font-extrabold text-lg" data-testid={`card-price-${id}`}>{price || "View pricing"}</span>
            <span className="text-ink-soft text-sm">{price ? " / night incl. GST" : ""}</span>
            {total && <span className="text-ink-faint text-sm"> · {total} total</span>}
          </p>
        </div>
      </Link>
      </div>
    </motion.div>
  );
};

export default PropertyCard;
